import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQuery";
import { CampaignId, ListResponse, RecommendedAccount } from "types/apiTypes";
import { RequestMethods } from "types/enums";
import {
  AccountFilters,
  TargetingConfiguration,
  UpdatedTargetingConfiguration,
} from "../types";

const path = "/recommended-accounts";

export enum RecommendedTags {
  recommended_accounts = "recommended-accounts",
  recommended_filters = "recommended-filters",
}

export const recommendedApi = createApi({
  reducerPath: "recommendedApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(RecommendedTags),
  endpoints: (builder) => ({
    getRecommendedAccounts: builder.query<
      ListResponse<RecommendedAccount>,
      CampaignId
    >({
      query: ({ campaign_id }) => ({
        url: `${path}/${campaign_id}`,
        method: RequestMethods.GET,
      }),
      providesTags: [RecommendedTags.recommended_accounts],
    }),

    generateRecommendedAccounts: builder.mutation<Response, CampaignId>({
      query: ({ campaign_id }) => ({
        url: `${path}/${campaign_id}/generate`,
        method: RequestMethods.POST,
      }),
      invalidatesTags: [RecommendedTags.recommended_accounts],
    }),

    addToTargetedList: builder.mutation<
      Response,
      { ids: string[] } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `${path}/${campaign_id}/promote`,
        method: RequestMethods.POST,
        body,
      }),
      invalidatesTags: [RecommendedTags.recommended_filters],
    }),

    removeRecommended: builder.mutation<
      Response,
      { ids: string[] } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `${path}/${campaign_id}`,
        method: RequestMethods.DELETE,
        body,
      }),
      invalidatesTags: [RecommendedTags.recommended_filters],
    }),

    exportRecommended: builder.mutation<
      Response,
      { file_name: string } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `${path}/${campaign_id}/export`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    getRecommendedFilters: builder.query<AccountFilters, CampaignId>({
      query: ({ campaign_id }) => ({
        url: `${path}/${campaign_id}/filters`,
        method: RequestMethods.GET,
      }),
      providesTags: [RecommendedTags.recommended_filters],
    }),

    getTargetingConfiguration: builder.query<
      TargetingConfiguration,
      CampaignId
    >({
      query: ({ campaign_id }) => ({
        url: `${path}/${campaign_id}/targeting-configuration`,
        method: RequestMethods.GET,
      }),
    }),

    updateTargetingConfiguration: builder.mutation<
      Response,
      UpdatedTargetingConfiguration
    >({
      query: ({ campaign_id, reset_ignored, files, ...rest }) => {
        const body = new FormData();
        body.append("reset_ignored", JSON.stringify(reset_ignored));

        files.forEach((file) => body.append("files", file));

        Object.entries(rest).forEach(([key, value]) => {
          value.forEach((el) =>
            body.append(key, typeof el === "string" ? el : JSON.stringify(el)),
          );
        });

        return {
          url: `${path}/${campaign_id}/targeting-configuration`,
          method: RequestMethods.PATCH,
          body,
        };
      },
      invalidatesTags: [RecommendedTags.recommended_filters],
    }),
  }),
});

export const {
  useGetRecommendedAccountsQuery,
  useGenerateRecommendedAccountsMutation,
  useAddToTargetedListMutation,
  useRemoveRecommendedMutation,
  useExportRecommendedMutation,
  useGetRecommendedFiltersQuery,
  useLazyGetTargetingConfigurationQuery,
  useUpdateTargetingConfigurationMutation,
} = recommendedApi;
